import {
  useEffect,
  useId,
  useMemo,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type Ref,
} from 'react';

import type { SearchPerson } from '../data/graph-types.js';
import { searchPeople } from '../lib/graph.js';

type SearchBoxProps = {
  people: readonly SearchPerson[];
  onSelect: (personId: string) => void;
  excludedPersonId?: string | null | undefined;
  inputRef?: Ref<HTMLInputElement> | undefined;
  label?: string | undefined;
  placeholder?: string | undefined;
  selectedPerson?: SearchPerson | null | undefined;
  suggestions?: readonly SearchPerson[] | undefined;
};

export function SearchBox({
  people,
  onSelect,
  excludedPersonId,
  inputRef,
  label = 'Search for a public figure',
  placeholder = 'Search a person',
  selectedPerson,
  suggestions = [],
}: SearchBoxProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const hasQuery = query.trim().length > 0;
  const results = useMemo(() => {
    const matches = hasQuery ? searchPeople(people, query) : suggestions;
    return matches.filter((person) => person.id !== excludedPersonId).slice(0, 8);
  }, [excludedPersonId, hasQuery, people, query, suggestions]);

  useEffect(() => {
    if (selectedPerson !== undefined) {
      setQuery(selectedPerson?.name ?? '');
      setOpen(false);
      setActiveIndex(0);
    }
  }, [selectedPerson]);

  const choose = (person: SearchPerson) => {
    setQuery(person.name);
    setOpen(false);
    setActiveIndex(0);
    onSelect(person.id);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = results[activeIndex] ?? results[0];
    if (result !== undefined) {
      choose(result);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(current + 1, Math.max(results.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Escape') {
      setOpen(false);
      if (!hasQuery) {
        event.currentTarget.blur();
      }
    }
  };

  return (
    <form
      className="search"
      role="search"
      aria-label={label}
      onSubmit={submit}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <span className="search__icon" aria-hidden="true">
        ⌕
      </span>
      <label className="sr-only" htmlFor={`${listId}-input`}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={`${listId}-input`}
        className="search__input"
        type="search"
        role="combobox"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-expanded={open && results.length > 0}
        aria-activedescendant={
          open && results[activeIndex] !== undefined
            ? `${listId}-${results[activeIndex].id}`
            : undefined
        }
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(0);
          setOpen(event.target.value.trim().length > 0 || suggestions.length > 0);
        }}
        onFocus={() => setOpen(hasQuery || suggestions.length > 0)}
        onKeyDown={handleKeyDown}
      />
      <span className="search__hint" aria-hidden="true">
        ↵ select
      </span>
      <span className="sr-only" role="status" aria-live="polite">
        {open
          ? hasQuery
            ? `${results.length} included ${results.length === 1 ? 'person' : 'people'} found`
            : `${results.length} featured starting ${results.length === 1 ? 'person' : 'people'}`
          : ''}
      </span>
      {open && results.length > 0 ? (
        <ul id={listId} className="search__results" role="listbox">
          {hasQuery ? null : (
            <li className="search__suggestions-label" role="presentation">
              Featured starting people
            </li>
          )}
          {results.map((person, index) => (
            <li key={person.id} role="presentation">
              <button
                id={`${listId}-${person.id}`}
                className="search__result"
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                onPointerDown={(event) => event.preventDefault()}
                onPointerEnter={() => setActiveIndex(index)}
                onClick={() => choose(person)}
              >
                <span className="search__result-star" aria-hidden="true" />
                <span className="search__result-copy">
                  <strong>
                    {person.name}
                    {person.inclusionRole === 'context' ? (
                      <span className="context-badge">Connection context</span>
                    ) : null}
                  </strong>
                  {person.description === undefined ? null : <small>{person.description}</small>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : open && hasQuery ? (
        <p className="search__empty" role="status">
          No included person found
        </p>
      ) : null}
    </form>
  );
}
